import {
  TAreaDeviceInput,
  TDeviceTypesInput,
  TFiniteDieselGeneratorInput,
  TInfiniteBusInput,
  TLoadInput,
  TMarketMakerInput,
  TPrepareDevicePayload,
  TPvInput,
  TStorageInput,
} from 'src/utils/mutationsGates/prepareAreaInput/prepareAreaInput.types';
import { TAssetType, TUploadedProfile } from 'src/typings/base-types';
import { TFileFieldTemplate, TFileFieldWithValue } from 'src/utils/assetsFields/assetsFields.types';

import { GridFeeType } from 'src/graphql';
import { convertHrsOfDay } from 'src/utils/assetsFields/valueConverters';
import { uploadedFilesCache } from 'src/cache/uploadedFiles';

/*
 *
 * Helpers
 *
 */
function getFile(file: TUploadedProfile | undefined) {
  if (typeof file === 'undefined') {
    return undefined;
  } else if (typeof file === 'string') {
    return file;
  } else {
    return uploadedFilesCache.get(file);
  }
}

function optional<T>(key: keyof T, object: Partial<T>) {
  if (key in object) {
    return { [key]: object[key] };
  }

  return null;
}

function optionalFile(
  key: TFileFieldTemplate['name'],
  value: TFileFieldWithValue['value'] | undefined,
) {
  if (value) {
    return { [key]: getFile(value) || value };
  }

  return null;
}

/*
 *
 * Prepare functions
 *
 */
function prepareArea({
  type,
  uuid,
  assetsValues,
  settingsData,
}: TPrepareDevicePayload): TAreaDeviceInput {
  type T = TAreaDeviceInput;
  const values = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('geoTagLocation', values),
    ...(settingsData?.gridFeeType === GridFeeType.Percentage &&
      optional<T>('gridFeePercentage', values)),
    ...(settingsData?.gridFeeType === GridFeeType.Constant &&
      optional<T>('gridFeeConstant', values)),
    ...optional<T>('importCapacityKva', values),
    ...optional<T>('exportCapacityKva', values),
    ...optional<T>('targetMarketKpi', values),
    ...optional<T>('baselinePeakEnergyImportKwh', values),
    ...optional<T>('baselinePeakEnergyExportKwh', values),
    ...optional<T>('baselinePeakEnergyExportKwh', values),
    // TODO: fitAreaBoundary probably needs work
    fitAreaBoundary: true,
    children: [],
  };

  return output;
}

function prepareFiniteDieselGenerator({
  type,
  uuid,
  assetsValues,
}: TPrepareDevicePayload): TFiniteDieselGeneratorInput {
  type T = TFiniteDieselGeneratorInput;
  const values = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('geoTagLocation', values),
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('energyRate', values),
    ...optional<T>('maxAvailablePowerKw', values),
  };

  return output;
}

function prepareLoad({ type, uuid, assetsValues }: TPrepareDevicePayload): TLoadInput {
  type T = TLoadInput;
  const { energyRateIncreasePerUpdate, ...values } = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    geoTagLocation: values.geoTagLocation,
    hrsPerDay: values.hrsPerDay,
    hrsOfDay: values.hrsOfDay
      ? convertHrsOfDay({ value: values.hrsOfDay, direction: 'forBE' })
      : null,
    ...optional<T>('avgPowerW', values),
    ...optional<T>('initialBuyingRate', values),
    ...optional<T>('finalBuyingRate', values),
    ...optional<T>('fitToLimit', values),
    ...optional<T>('updateInterval', values),
    ...optional<T>('useMarketMakerRate', values),
    ...optionalFile('dailyLoadProfile', values.dailyLoadProfile),
    profileUUID: values.dailyLoadProfileUuid,
    libraryUUID: values.libraryUUID,
    // targetDeviceKpi: ,
    // forecastStreamEnabled: ,
  };

  if (!output.fitToLimit) {
    if (energyRateIncreasePerUpdate && energyRateIncreasePerUpdate !== 'Varying rate') {
      output.energyRateIncreasePerUpdate = energyRateIncreasePerUpdate;
    }
  }

  return output;
}

function preparePV({ type, uuid, assetsValues }: TPrepareDevicePayload): TPvInput {
  type T = TPvInput;
  const { energyRateDecreasePerUpdate, ...values } = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('geoTagLocation', values),
    ...optional<T>('capacityKw', values),
    ...optional<T>('initialSellingRate', values),
    ...optional<T>('finalSellingRate', values),
    ...optional<T>('fitToLimit', values),
    ...optional<T>('updateInterval', values),
    ...optional<T>('cloudCoverage', values),
    ...optionalFile('powerProfile', values.powerProfile),
    profileUUID: values.powerProfileUuid,
    libraryUUID: values.libraryUUID,
    ...optional<T>('useMarketMakerRate', values),
    // targetDeviceKpi: ,
    // forecastStreamEnabled: ,
  };

  if (!output.fitToLimit) {
    if (energyRateDecreasePerUpdate && energyRateDecreasePerUpdate !== 'Varying rate') {
      output.energyRateDecreasePerUpdate = energyRateDecreasePerUpdate;
    }
  }

  return output;
}

function prepareStorage({ type, uuid, assetsValues }: TPrepareDevicePayload): TStorageInput {
  type T = TStorageInput;
  const { energyRateIncreasePerUpdate, energyRateDecreasePerUpdate, ...values } = assetsValues[
    uuid
  ];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('geoTagLocation', values),
    ...optional<T>('initialSoc', values),
    ...optional<T>('minAllowedSoc', values),
    ...optional<T>('batteryCapacityKwh', values),
    ...optional<T>('maxAbsBatteryPowerKw', values),
    ...optional<T>('capPriceStrategy', values),
    ...optional<T>('initialSellingRate', values),
    ...optional<T>('finalSellingRate', values),
    ...optional<T>('initialBuyingRate', values),
    ...optional<T>('finalBuyingRate', values),
    ...optional<T>('fitToLimit', values),
    ...optional<T>('updateInterval', values),
    // targetDeviceKpi: ,
  };

  if (!output.fitToLimit) {
    if (typeof energyRateIncreasePerUpdate === 'number') {
      output.energyRateIncreasePerUpdate = energyRateIncreasePerUpdate;
    }

    if (typeof energyRateDecreasePerUpdate === 'number') {
      output.energyRateDecreasePerUpdate = energyRateDecreasePerUpdate;
    }
  }

  return output;
}

function prepareMarketMaker({
  type,
  uuid,
  assetsValues,
}: TPrepareDevicePayload): TMarketMakerInput {
  type T = TMarketMakerInput;
  const values = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('geoTagLocation', values),
    ...optional<T>('energyRate', values),
    ...optionalFile('energyRateProfile', values.energyRateProfile),
    profileUUID: values.energyRateProfileUuid,
    libraryUUID: values.libraryUUID,
    ...optional<T>('gridConnected', values),
  };

  return output;
}

function prepareInfiniteBus({
  type,
  uuid,
  assetsValues,
}: TPrepareDevicePayload): TInfiniteBusInput {
  type T = TInfiniteBusInput;
  const values = assetsValues[uuid];

  const output: T = {
    type,
    uuid,
    name: values.name || '',
    ...optional<T>('allowExternalConnection', values),
    ...optional<T>('geoTagLocation', values),
    ...optional<T>('energyRate', values),
    ...optional<T>('energyBuyRate', values),
    libraryUUID: values.libraryUUID,
  };

  return output;
}

export const prepareDeviceInputFunctions: {
  [key in TAssetType]: (payload: TPrepareDevicePayload) => TDeviceTypesInput;
} = {
  Area: prepareArea,
  FiniteDieselGenerator: prepareFiniteDieselGenerator,
  Load: prepareLoad,
  PV: preparePV,
  Storage: prepareStorage,
  MarketMaker: prepareMarketMaker,
  InfiniteBus: prepareInfiniteBus,
};
